export enum QboConnectionStatus {
  Active = 'active',
  Disconnected = 'disconnected',
  Error = 'error',
}

export enum QboCollectionLogStatus {
  // In-flight: the log row is reserved (via the unique collection index) BEFORE
  // the QBO deposit POST, then resolved to Posted/Failed. A row left in this
  // state means a prior attempt died after reserving but before resolving — the
  // next sync reconciles it against QBO rather than blindly re-posting.
  Posting = 'posting',
  Posted = 'posted',
  Failed = 'failed',
  // Needs attention, NOT retryable: the collection is dated in a period QuickBooks
  // has closed, so a post would be rejected (Intuit fault 5030). We record it with
  // a plain-language reason and never re-POST it — it self-heals only if the
  // closing date later moves so the period reopens. Distinct from Failed, which
  // the next sync retries.
  Blocked = 'blocked',
  // A Blocked row the client explicitly acknowledged and cleared out of the
  // needs-attention list ("I backfilled into a closed period by mistake and I'm not
  // going to reopen it"). It is NOT terminal: dismissal silences the row while the
  // period stays closed, but if the period is later reopened the collection falls
  // through to a normal post like any other unlocked row. A dismiss must never
  // permanently drop a real collection out of the client's books.
  Dismissed = 'dismissed',
  // Deliberately not synced and not retryable: the collection is something this
  // integration doesn't push (today: a zero amount, which has no entry to post in
  // either direction — negative amounts DO post, as chargeback reversals). Recorded
  // with a plain-language reason so it appears in the log instead of vanishing —
  // QuickBooks was never called, so this is neither a failure nor a rejection.
  Skipped = 'skipped',
}

// Whether pushed collections are attributed to a QBO customer. Customer identity
// is resolved through the confirmed entity→QBO-customer mapping (qbo_customer_mapping);
// `match-only` stamps the matched customer name on the journal entry, `none` posts
// untagged. (Invoice-level AR application is intentionally out of scope.)
export enum QboCustomerMatchingMode {
  MatchOnly = 'match-only',
  None = 'none',
}

// Whether collections sync on a schedule or only on demand
export enum QboSyncMode {
  Manual = 'manual',
  Auto = 'auto',
}

// The five kinds of loan activity a client can independently switch on, each
// posting against the connection's single loan (control) account on one side and
// its own configured account on the other. Every entry this integration writes is
// `loan account <-> one other account`, so pulling the loan account out as a
// one-time setting leaves exactly one account to configure per category.
//
// These are NOT Atlas's fee taxonomy. They are the groupings Dwight's own GL
// already uses (FeeTypeCodeMapping in interfaces/loanServicing), which is also the
// only granularity clients' charts of accounts actually distinguish: an unused line
// fee is booked as interest, a facility fee stands alone and is usually capitalized,
// and no client anywhere separates wire from lockbox from termination fees.
export enum QboSyncCategory {
  Collections = 'collections',
  Fundings = 'fundings',
  Interest = 'interest',
  FacilityFees = 'facility-fees',
  OtherFees = 'other-fees',
}

// QBO Account.Classification — the side of the chart an account sits on. Filtering
// on Classification (not AccountType) means a client's account is never excluded
// just because of its subtype (Bank vs Other Current Asset, etc.).
//
// Expense exists for the fee and interest pickers, but those pickers deliberately
// offer Liability — and, for facility fees, Asset — alongside it. Clients routinely
// book Dwight fees to a balance-sheet account paired with the loan ("Dwight ABL
// Loan Fees", an Other Current Liability; "Dwight Working Line_Finance Fee", a Long
// Term Liability), and an origination/facility fee is usually capitalized as a
// deferred financing cost and amortized rather than expensed. Restricting those
// pickers to Expense alone would hide the account the client already uses.
export enum QboAccountClassification {
  Asset = 'Asset',
  Liability = 'Liability',
  Expense = 'Expense',
}

// A selectable QBO account for the clearing/loan pickers. `type` is the QBO
// AccountType (e.g. "Bank", "Other Current Asset") shown as a hint in the UI.
export interface IQboAccountOption {
  id: string
  name: string
  classification: QboAccountClassification
  type: string
}

// Account lists for the settings pickers, split by Classification. Assets feed the
// clearing and funding-deposit pickers, Liabilities the loan picker, and the fee /
// interest pickers draw on expenses plus liabilities (plus assets for facility
// fees) — see QboAccountClassification for why those are not expense-only.
export interface IQboSelectableAccounts {
  assets: IQboAccountOption[]
  liabilities: IQboAccountOption[]
  expenses: IQboAccountOption[]
}

// A QBO customer option for the entity→customer mapping picker.
export interface IQboCustomerOption {
  id: string
  name: string
}

// One row of the entity→QBO-customer mapping screen: a Dwight entity we collect
// from for this client, its current mapping (if any), and an autofill suggestion.
export interface IQboCustomerMappingRow {
  entityId: string
  entityName: string
  aliases: string[]
  qboCustomerId: string | null
  qboCustomerName: string | null
  // Account this customer's payments post to instead of the Collections account.
  qboAccountId: string | null
  qboAccountName: string | null
  // Best name-match suggestion when no confirmed mapping exists yet.
  suggestedQboCustomerId: string | null
  suggestedQboCustomerName: string | null
}

// A single mapping change submitted from the mapping screen. The customer and the
// account save independently: a pair left out of the input keeps its stored value.
export interface IQboCustomerMappingInput {
  entityId: string
  qboCustomerId?: string | null
  qboCustomerName?: string | null
  qboAccountId?: string | null
  qboAccountName?: string | null
}

// Safe projection of a qbo_connection row — never includes OAuth tokens
export interface IQboConnectionInfo {
  id: string
  clientName: string
  realmId: string
  status: QboConnectionStatus
  // Asset account collections are deposited into (Bank or Other Current Asset)
  qboClearingAccountId: string | null
  qboClearingAccountName: string | null
  // Liability account the paydown debits (the client's Dwight loan/LOC). This is the
  // control account: every entry, in every category, posts against it on one side.
  qboLoanAccountId: string | null
  qboLoanAccountName: string | null
  // Per-category sync switches. Collections is the only one on by default, so
  // nothing about an existing client's books changes when the rest ship.
  isCollectionsSyncEnabled: boolean
  isFundingsSyncEnabled: boolean
  isInterestSyncEnabled: boolean
  isFacilityFeesSyncEnabled: boolean
  isOtherFeesSyncEnabled: boolean
  // Per-category counter-accounts. Collections deliberately has none of its own —
  // it uses qboClearingAccount* above, which is both the pre-existing column and
  // the more accurate name for what it is. QBO_SYNC_CATEGORY_FIELDS owns that one
  // exception so every caller still sees a uniform category -> account lookup.
  //
  // Fundings deposit into the client's operating bank account, NOT the clearing
  // account: clearing holds collections on their way to Dwight, while a funding is
  // cash arriving from Dwight.
  qboFundingAccountId: string | null
  qboFundingAccountName: string | null
  qboInterestAccountId: string | null
  qboInterestAccountName: string | null
  qboFacilityFeeAccountId: string | null
  qboFacilityFeeAccountName: string | null
  qboOtherFeesAccountId: string | null
  qboOtherFeesAccountName: string | null
  customerMatchingMode: QboCustomerMatchingMode | null
  syncMode: QboSyncMode | null
  lastSyncedAt: string | null
  // The closing date QuickBooks last reported: everything dated on or before it is
  // locked and can't be posted. Shown next to the sync controls so the client picks
  // a start date that won't be rejected. Null with a `bookCloseDateRefreshedAt`
  // means QuickBooks reports no closing date; null with both null means we have
  // never read one (never synced, never refreshed) — don't claim the books are open.
  bookCloseDate: string | null
  bookCloseDateRefreshedAt: string | null
  cacheRefreshedAt: string | null
  errorCode: string | null
  errorMessage: string | null
  lastRefreshedAt: string | null
  createdAt: string
}

export interface IQboCollectionLog {
  id: string
  qboConnectionId: string
  clientName: string
  activityId: string
  activityType: string
  qboTxnId: string | null
  // JE number sent to QBO as DocNumber; null for entries posted before numbering.
  qboDocNumber: string | null
  qboTxnType: string
  amount: string
  recordDate: string
  status: QboCollectionLogStatus
  errorMessage: string | null
  // Intuit fault code from the last failed post (e.g. 5030 = closing-date lock)
  errorCode: string | null
  // Signature of the data last pushed to QBO; drives correction detection.
  syncedHash: string | null
  postedAt: string | null
  isPending: boolean
  createdAt: string
}

// Status rollup for the Sync Activity view. `pending` is a Posted row still
// carrying the (PENDING) marker for the current open day; `posted` excludes it.
export interface IQboCollectionLogSummary {
  posted: number
  pending: number
  failed: number
  blocked: number
  // Blocked rows the client dismissed. Counted separately so they drop out of the
  // needs-attention headline without disappearing from the log entirely.
  dismissed: number
  // Recorded as deliberately not synced (e.g. a zero amount) — shown in the log and
  // the summary line, but not part of the needs-attention set.
  skipped: number
}

// Shape returned by the collection-log list endpoint: a page of rows for the
// full log, plus a status summary and the (small) needs-attention set so the view
// never has to page to render its headline numbers.
//
// `summary` and `needsAttention` describe the ACTIVE connection only, so their
// numbers never merge two QuickBooks companies. The paged `data` stays
// whole-client: a company the client used to be connected to is still part of
// their history, so those rows are shown and labelled (each row carries its
// `qboConnectionId`, compare against `activeQboConnectionId`) rather than hidden.
export interface IQboCollectionLogList {
  data: IQboCollectionLog[]
  totals: { totalItems: number }
  summary: IQboCollectionLogSummary
  needsAttention: IQboCollectionLog[]
  // The connection the summary and needs-attention set are scoped to; null when
  // the client has no active QuickBooks connection.
  activeQboConnectionId: string | null
  // Log rows NOT on the active connection — history from a previously-connected
  // company, counted separately so it can be labelled instead of silently folded
  // into the headline numbers.
  historyItems: number
}

export interface IQboLogCollectionsResult {
  posted: number
  finalized: number
  removed: number
  failed: number
  // Collections dated in a QuickBooks-closed period: recorded as Blocked, never posted.
  blocked: number
  // Closed-period collections the client had already dismissed. Tracked apart from
  // `blocked` because that count drives the run's warning state and the QboSyncFailed
  // notification — a dismissed row must not re-alert on every subsequent sync.
  dismissed: number
  // Already-synced collections whose Atlas data changed and were re-pushed to QBO.
  corrected: number
  skipped: { activityId: string; reason: string }[]
  dryRun: boolean
}
